import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import ReservationForm from '../components/ReservationForm';
import PoweredBy from '../components/PoweredBy';
import { getRestaurantById } from '../services/restaurantService';
import { fetchRestaurantBySlug } from '../services/guestPortalService';
import { getEventById } from '../services/eventService';
import { getShiftById } from '../services/shiftService';
import { normalizeNoteHtml } from '../utils/richText';
import { setPageSeo } from '../utils/seo';
import '../styles/widget2.css';

const BASE_API = process.env.REACT_APP_API_URL || 'http://localhost:3000/api/v1';

/**
 * ReservationWidget - Standalone widget page for embedding in external websites via iframe
 * This page contains only the reservation form without navbar, footer, or any other UI elements
 * Query params:
 * - restaurantId: The ID of the restaurant (required)
 * - eventId / shiftId: optional pre-selection
 * - primaryColor / secondaryColor / accentColor / textColor / backgroundColor:
 *     optional hex overrides for the widget theme (fall back to the restaurant's
 *     own widget colors when absent)
 *
 * - embed: pass "1" to opt into host-embed mode — the widget then blends into
 *     the host page (transparent background, no outer padding) and posts
 *     "eez7-reservation" messages to the parent frame (type: 'resize' with a
 *     pixel height, and 'submitted' on success) so the host can size the frame to
 *     fit. Existing plain iframe embeds that don't pass this flag are unaffected.
 */
const post = (msg) => {
  try {
    if (typeof window !== 'undefined' && window.parent && window.parent !== window) {
      window.parent.postMessage({ source: 'eez7-reservation', ...msg }, '*');
    }
  } catch (e) {
    /* noop */
  }
};

const ReservationWidget2 = () => {
  const [searchParams] = useSearchParams();
  const [restaurant, setRestaurant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [isEventDate, setIsEventDate] = useState(false);
  const [eventHouseRules, setEventHouseRules] = useState('');
  // On experience dates: null until the guest picks a booking type, then
  // 'regular' or 'tickets'. Drives which top-of-widget note is shown.
  const [bookingChoice, setBookingChoice] = useState(null);
  const [initialEvent, setInitialEvent] = useState(null);
  const [eventAccessDenied, setEventAccessDenied] = useState(null);
  // initialEventShift is consumed once by the form for pre-selection (then cleared);
  // displayEventShift is the persistent banner data shown in the widget UI.
  const [initialEventShift, setInitialEventShift] = useState(null);
  const [displayEventShift, setDisplayEventShift] = useState(null);

  const [prefillUser, setPrefillUser] = useState(null);

  // Two ways in:
  //   /widget2/:slug          — the shareable link (same slugs as the guest portal)
  //   /widget2?restaurantId=N — the original form, kept for existing embeds.
  const { slug } = useParams();
  const queryRestaurantId = searchParams.get('restaurantId');
  const [slugRestaurantId, setSlugRestaurantId] = useState(null);
  const [slugNotFound, setSlugNotFound] = useState(false);
  const restaurantId = slug ? slugRestaurantId : queryRestaurantId;
  // True while we're turning the slug into an id — the "missing restaurant"
  // error must not flash during that round-trip.
  const resolvingSlug = !!slug && !slugRestaurantId && !slugNotFound;

  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    setSlugNotFound(false);
    fetchRestaurantBySlug(slug)
      .then((r) => {
        if (cancelled) return;
        if (r?.id) setSlugRestaurantId(String(r.id));
        else setSlugNotFound(true);
      })
      .catch(() => { if (!cancelled) setSlugNotFound(true); });
    return () => { cancelled = true; };
  }, [slug]);

  const eventId = searchParams.get('eventId');
  const shiftId = searchParams.get('shiftId');
  // Host-embed mode (opt-in). Only set by trusted hosts that size the iframe via
  // the resize messages below; plain external embeds leave it off and are unchanged.
  const embedded = searchParams.get('embed') === '1';
  // JWT handed in by the host (e.g. a signed-in Kiki's user). The widget runs in a
  // cross-origin iframe, so AuthContext can't see the host's localStorage — we use
  // this token to fetch the profile and prefill the form's contact fields.
  const token = searchParams.get('token');

  useEffect(() => {
    if (resolvingSlug) return;   // wait for the slug lookup
    if (!restaurantId) {
      setError(slug ? 'Restaurant not found' : 'Restaurant ID is required');
      setLoading(false);
      return;
    }

    const loadRestaurant = async () => {
      try {
        setLoading(true);
        const data = await getRestaurantById(restaurantId);
        setRestaurant(data);
        setError(null);
      } catch (err) {
        console.error('Error loading restaurant:', err);
        setError('Failed to load restaurant information');
      } finally {
        setLoading(false);
      }
    };

    loadRestaurant();
  }, [restaurantId, resolvingSlug, slug]);

  // Per-page SEO once the restaurant is known. The canonical is always the slug
  // link, also for the legacy ?restaurantId= form (same page, same restaurant);
  // a restaurant without a slug gets no canonical rather than the bare /widget2.
  useEffect(() => {
    if (!restaurant?.name) return;
    setPageSeo({
      title: `Reserve a table at ${restaurant.name} | ez restaurants`,
      description: `Book a table at ${restaurant.name} online — pick a date, time and party size and get an instant confirmation.`,
      canonicalPath: slug || restaurant.slug ? `/widget2/${slug || restaurant.slug}` : undefined,
    });
  }, [restaurant, slug]);

  // Fetch event if eventId is provided in URL
  useEffect(() => {
    if (!eventId) return;
    const loadEvent = async () => {
      try {
        const data = await getEventById(eventId);
        if (data.accessDenied) {
          setEventAccessDenied(data.message || 'This is a private experience. You don\'t have access.');
          setInitialEvent(null);
          return;
        }
        setEventAccessDenied(null);
        if (data.event) {
          setInitialEvent(data.event);
        }
      } catch (err) {
        console.error('Error loading event:', err);
      }
    };
    loadEvent();
  }, [eventId]);

  // Fetch event shift if shiftId is provided in URL
  useEffect(() => {
    if (!shiftId) return;
    const loadShift = async () => {
      try {
        const data = await getShiftById(shiftId);
        if (data) {
          setInitialEventShift(data);
          setDisplayEventShift(data);
        }
      } catch (err) {
        console.error('Error loading shift:', err);
      }
    };
    loadShift();
  }, [shiftId]);

  // Fetch the signed-in user's profile (when a host token is provided) so the
  // form can prefill First/Last name, Email and Phone. Best-effort: on any
  // failure the form simply stays blank and works as a normal guest booking.
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    const loadProfile = async () => {
      try {
        const res = await axios.get(`${BASE_API}/users/profile/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        const body = res.data?.data ?? res.data;
        const u = body?.user ?? body;
        if (!u) return;
        setPrefillUser({
          firstName: u.firstName || '',
          lastName: u.lastName || '',
          email: u.email || '',
          phone: u.phoneNumber || u.phone || '',
        });
      } catch (err) {
        // Silent — prefill is a convenience, not a requirement.
      }
    };
    loadProfile();
    return () => {
      cancelled = true;
    };
  }, [token]);

  // In host-embed mode, blend into the host page by dropping the standalone
  // page's grey background so the host's own surface shows through.
  useEffect(() => {
    if (!embedded || typeof document === 'undefined') return;
    document.documentElement.style.background = 'transparent';
    document.body.style.background = 'transparent';
  }, [embedded]);

  // Auto-resize: tell the host iframe how tall the content is so it can size the
  // frame to fit (no nested scrollbar). Only active in embed mode. Measures the
  // wrapper, which in embed mode has its min-height:100vh removed so the height
  // reflects content, not the iframe — avoiding a grow-on-every-post feedback loop.
  useEffect(() => {
    if (!embedded || typeof window === 'undefined' || !('ResizeObserver' in window)) return;
    const target = document.querySelector('.w2');
    if (!target) return;
    let lastSent = 0;
    const sendHeight = () => {
      const h = Math.ceil(target.getBoundingClientRect().height);
      if (h > 0 && Math.abs(h - lastSent) > 2) {
        lastSent = h;
        post({ type: 'resize', height: h });
      }
    };
    const ro = new ResizeObserver(sendHeight);
    ro.observe(target);
    sendHeight();
    return () => ro.disconnect();
  }, [embedded, restaurant, loading, error, showSuccess, isEventDate, displayEventShift, eventAccessDenied]);

  // Notify the host when a reservation completes.
  useEffect(() => {
    if (embedded && showSuccess) post({ type: 'submitted' });
  }, [embedded, showSuccess]);

  if (resolvingSlug) {
    return (
      <div className="w2 w2-state">
        <div className="w2-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  if (!restaurantId) {
    return (
      <div className="w2">
        <div className="w2-state is-error">
          <p>
            {slug ? 'We could not find that restaurant.' : 'Missing restaurant. Use /widget2/<name> or add ?restaurantId= to the URL.'}
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="w2 w2-state">
        <div className="w2-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w2">
        <div className="w2-state is-error">
          <p>{error}</p>
        </div>
      </div>
    );
  }

  if (!restaurant) {
    return (
      <div className="w2">
        <div className="w2-state is-error">
          <p>Restaurant not found</p>
        </div>
      </div>
    );
  }

  if (eventAccessDenied) {
    const isLoggedIn = !!localStorage.getItem('eez7_client_token');
    return (
      <div className="w2">
        <div className="w2-state">
          <div className="w2-lock">
            <i className="bi bi-lock-fill"></i>
          </div>
          <h3>Private Experience</h3>
          <p>{eventAccessDenied}</p>
          {!isLoggedIn && (
            <a
              href={`/login?returnUrl=${encodeURIComponent(window.location.pathname + window.location.search)}`}
              className="btn btn-primary"
            >
              Sign in to check access
            </a>
          )}
        </div>
      </div>
    );
  }

  // Build custom color styles: query-param overrides (host theming) take
  // precedence, then the restaurant's own widget settings, then hard defaults.
  const themeColor = (param, restaurantValue, fallback) =>
    searchParams.get(param) || restaurantValue || fallback;
  const primary   = themeColor('primaryColor',    restaurant.widgetPrimaryColor,    '#14203C');
  const secondary = themeColor('secondaryColor',  restaurant.widgetSecondaryColor,  '#2C4079');
  const accent    = themeColor('accentColor',     restaurant.widgetAccentColor,     '#E8B04B');
  const text      = themeColor('textColor',       restaurant.widgetTextColor,       '#1B2333');
  const surface   = themeColor('backgroundColor', restaurant.widgetBackgroundColor, '#FFFFFF');
  const customColorStyles = {
    '--w2-primary': primary, '--w2-primary-2': secondary, '--w2-accent': accent,
    '--w2-text': text, '--w2-surface': surface,
    // legacy vars the shared form CSS still reads
    '--primary-purple': primary, '--secondary-purple': secondary, '--accent-lime': accent,
    '--text-dark': text, '--white': surface, '--light-purple': secondary, '--bg-light': surface,
  };

  return (
    <div className={`w2${embedded ? ' is-embedded' : ''}`} style={customColorStyles}>
      {/* Restaurant hero */}
      <div className={`w2-hero${restaurant.imageUrl ? '' : ' no-image'}`}>
        {restaurant.imageUrl && (
          <img src={`${process.env.REACT_APP_BACKEND_URL || ''}${restaurant.imageUrl}`} alt="" className="w2-hero-img" />
        )}
        <div className="w2-hero-body">
          <h1 className="w2-hero-name">{restaurant.name}</h1>
          {(restaurant.location || (restaurant.phoneNumbers && restaurant.phoneNumbers.length > 0)) && (
            <div className="w2-hero-meta">
              {restaurant.location && (
                <span><i className="bi bi-geo-alt-fill"></i> {restaurant.location}</span>
              )}
              {restaurant.phoneNumbers && restaurant.phoneNumbers.map((phone, index) => (
                <a key={index} href={`tel:${phone}`}><i className="bi bi-telephone-fill"></i> {phone}</a>
              ))}
            </div>
          )}
        </div>
      </div>
      {/* Widget Note */}
      {restaurant.widgetNote && restaurant.widgetNote.replace(/<[^>]*>/g, '').trim() && !showSuccess && (!isEventDate || bookingChoice === 'regular') && (
        <div className="w2-note">
          <div
            dangerouslySetInnerHTML={{ __html: normalizeNoteHtml(restaurant.widgetNote) }}
          />
        </div>
      )}
      {/* Event House Rules - shown only on event dates */}
      {eventHouseRules && eventHouseRules.replace(/<[^>]*>/g, '').trim() && !showSuccess && (
        <div className="w2-note">
          <div
            dangerouslySetInnerHTML={{ __html: normalizeNoteHtml(eventHouseRules) }}
          />
        </div>
      )}
      {/* Event Shift hero banner — image + description for shifts opened via copy-link */}
      {displayEventShift && displayEventShift.shiftType === 'event' && !showSuccess && (
        <div className="w2-shift-banner">
          {displayEventShift.imageUrl && (
            <div className="w2-shift-banner-img">
              <img
                src={`${process.env.REACT_APP_BACKEND_URL || ''}${displayEventShift.imageUrl}`}
                alt={displayEventShift.label}
              />
            </div>
          )}
          <h4>{displayEventShift.label}</h4>
          {displayEventShift.description && <p>{displayEventShift.description}</p>}
        </div>
      )}
      <ReservationForm
        restaurantId={restaurantId}
        restaurantName={restaurant.name}
        restaurant={restaurant}
        isWidget={true}
        prefillUser={prefillUser}
        /* Host-authenticated = the Kiki's embed handed us a valid JWT that
           resolved to a profile. Used to skip the guest email OTP for logged-in
           Kiki's users; plain widget embeds (no host token) keep verifying. */
        hostAuthenticated={embedded && !!prefillUser}
        initialEvent={initialEvent}
        onInitialEventHandled={() => setInitialEvent(null)}
        initialEventShift={initialEventShift}
        onInitialEventShiftHandled={() => setInitialEventShift(null)}
        onSuccessChange={setShowSuccess}
        onEventDateChange={setIsEventDate}
        onEventHouseRulesChange={setEventHouseRules}
        onBookingChoiceChange={setBookingChoice}
      />
      <PoweredBy hidden={!!restaurant?.organization?.hidePoweredBy} />
    </div>
  );
};

export default ReservationWidget2;
